import React, { useState, useEffect } from 'react';
import axios from 'axios';
import api from '../config/api';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';
import Navigation from './Navigation';
import './Stats.css';
import { API_BASE_URL } from '../config/api';

// Colors for up to 8 users in the per-user chart
const USER_COLORS = ['#4caf50', '#2196f3', '#ff9800', '#e91e63', '#9c27b0', '#00bcd4', '#ff5722', '#607d8b'];

// Colors for the two search types
const SEARCH_TYPE_COLORS = { scheduled: '#4caf50', instant: '#2196f3' };

/**
 * Pivot flat rows from the search-stats API into recharts-friendly data.
 * When grouping by user: rows have { period, user_name, search_count }
 * When grouping by type: rows have { period, search_type, search_count }
 */
function pivotSearchData(rows, labelKey) {
    const periodsMap = {};
    const groups = [];
    const groupSet = new Set();

    rows.forEach(row => {
        // Dates come back as Date objects or 'YYYY-MM-DD' strings
        const period = row.period instanceof Date
            ? row.period.toISOString().split('T')[0]
            : String(row.period).split('T')[0];
        const group = row[labelKey];

        if (!periodsMap[period]) periodsMap[period] = { period };
        periodsMap[period][group] = (periodsMap[period][group] || 0) + parseInt(row.search_count || 0);

        if (!groupSet.has(group)) {
            groupSet.add(group);
            groups.push(group);
        }
    });

    return { chartData: Object.values(periodsMap), groups };
}

function formatSearchPeriodLabel(period, view) {
    // period is always 'YYYY-MM-DD' at this point
    const d = new Date(period + 'T00:00:00');
    if (view === 'day') {
        return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
    if (view === 'week') {
        return 'Wk ' + d.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
    // month
    return d.toLocaleDateString([], { year: 'numeric', month: 'short' });
}

function Stats() {
    const [queueData, setQueueData] = useState(null);
    const [keepaData, setKeepaData] = useState(null);
    const [searchStatsData, setSearchStatsData] = useState(null);
    const [chartData, setChartData] = useState([]);
    const [queueChartData, setQueueChartData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [keepaLoading, setKeepaLoading] = useState(true);
    const [searchStatsLoading, setSearchStatsLoading] = useState(true);
    const [chartLoading, setChartLoading] = useState(false);
    const [queueChartLoading, setQueueChartLoading] = useState(false);
    const [error, setError] = useState(null);
    const [timePeriod, setTimePeriod] = useState('6h');
    const [queueTimePeriod, setQueueTimePeriod] = useState('2h');
    const [chartInterval, setChartInterval] = useState('hour');
    const [queueChartInterval, setQueueChartInterval] = useState('5min');

    // Search activity chart state
    const [searchView, setSearchView] = useState('day'); // 'day' | 'week' | 'month'
    const [searchUsers, setSearchUsers] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [searchChartData, setSearchChartData] = useState([]);
    const [searchGroups, setSearchGroups] = useState([]);
    const [searchChartLoading, setSearchChartLoading] = useState(false);
    const [searchChartError, setSearchChartError] = useState(null);

    // ScrapingDog daily calls chart state (current month)
    const [scrapingDogChartData, setScrapingDogChartData] = useState([]);
    const [scrapingDogChartLoading, setScrapingDogChartLoading] = useState(false);
    const [scrapingDogChartError, setScrapingDogChartError] = useState(null);

    // Time period options for performance chart
    const timePeriodOptions = [
        { value: '6h', label: 'Last 6 Hours' },
        { value: '12h', label: 'Last 12 Hours' },
        { value: '24h', label: 'Last 24 Hours' },
        { value: '3d', label: 'Last 3 Days' },
        { value: '7d', label: 'Last 7 Days' },
        { value: '30d', label: 'Last 30 Days' }
    ];

    // Time period options for queue chart (shorter intervals)
    const queueTimePeriodOptions = [
        { value: '1h', label: 'Last 1 Hour' },
        { value: '2h', label: 'Last 2 Hours' },
        { value: '6h', label: 'Last 6 Hours' },
        { value: '12h', label: 'Last 12 Hours' },
        { value: '24h', label: 'Last 24 Hours' },
        { value: '3d', label: 'Last 3 Days' },
        { value: '7d', label: 'Last 7 Days' },
        { value: '30d', label: 'Last 30 Days' }
    ];

    // Determine queue interval based on time period
    const determineQueueInterval = (period) => {
        switch (period) {
            case '1h':
            case '2h':
                return '5min';
            case '6h':
            case '12h':
            case '24h':
                return '30min';
            case '3d':
            case '7d':
                return '6hour';
            case '30d':
            default:
                return 'day';
        }
    };

    // Fetch queue size
    const fetchQueueSize = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/spapi/new-stats/queue-size`);
            setQueueData(response.data);
            setError(null);
        } catch (err) {
            console.error('Error fetching queue size:', err);
            setError('Failed to fetch queue size');
        } finally {
            setLoading(false);
        }
    };

    // Fetch Keepa stats
    const fetchKeepaStats = async () => {
        try {
            const response = await api.get('/api/keepa/stats/queue-size');
            setKeepaData(response.data);
        } catch (err) {
            console.error('Error fetching Keepa stats:', err);
        } finally {
            setKeepaLoading(false);
        }
    };

    // Fetch search stats
    const fetchSearchStatsData = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/spapi/new-stats/search-stats`);
            setSearchStatsData(response.data);
        } catch (err) {
            console.error('Error fetching search stats:', err);
        } finally {
            setSearchStatsLoading(false);
        }
    };

    // Fetch detailed stats
    const fetchStatsDetails = async (period, chartIntervalType) => {
        setChartLoading(true);
        try {
            // Calculate start and end timestamps based on period
            const end = new Date();
            const start = new Date();
            
            switch (period) {
                case '6h':
                    start.setHours(start.getHours() - 6);
                    break;
                case '12h':
                    start.setHours(start.getHours() - 12);
                    break;
                case '24h':
                    start.setHours(start.getHours() - 24);
                    break;
                case '3d':
                    start.setDate(start.getDate() - 3);
                    break;
                case '7d':
                    start.setDate(start.getDate() - 7);
                    break;
                case '30d':
                    start.setDate(start.getDate() - 30);
                    break;
                default:
                    start.setHours(start.getHours() - 6);
            }

            const response = await axios.get(`${API_BASE_URL}/api/spapi/new-stats/details`, {
                params: {
                    start: start.toISOString(),
                    end: end.toISOString(),
                    interval: chartIntervalType
                }
            });

            // Format data for recharts
            const formattedData = response.data.map(item => ({
                timestamp: chartIntervalType === 'day' 
                    ? new Date(item.date).toLocaleDateString()
                    : new Date(item.timestamp).toLocaleString([], { 
                        month: 'short', 
                        day: 'numeric', 
                        hour: '2-digit', 
                        minute: '2-digit' 
                    }),
                processed: parseInt(item.processed_requests || 0),
                failed: parseInt(item.failed_requests || 0),
                avgResponseTime: parseFloat(item.avg_response_time_sec || 0),
                maxResponseTime: parseFloat(item.max_response_time_sec || 0),
                avgAttempts: parseFloat(item.avg_attempts || 0),
                avgQueueSize: parseFloat(item.avg_queue_size || 0)
            }));

            setChartData(formattedData);
            setError(null);
        } catch (err) {
            console.error('Error fetching stats details:', err);
            setError('Failed to fetch stats details');
        } finally {
            setChartLoading(false);
        }
    };

    // Fetch queue history
    const fetchQueueHistory = async (period, queueIntervalType) => {
        setQueueChartLoading(true);
        try {
            // Calculate start and end timestamps based on period
            const end = new Date();
            const start = new Date();
            
            switch (period) {
                case '1h':
                    start.setHours(start.getHours() - 1);
                    break;
                case '2h':
                    start.setHours(start.getHours() - 2);
                    break;
                case '6h':
                    start.setHours(start.getHours() - 6);
                    break;
                case '12h':
                    start.setHours(start.getHours() - 12);
                    break;
                case '24h':
                    start.setHours(start.getHours() - 24);
                    break;
                case '3d':
                    start.setDate(start.getDate() - 3);
                    break;
                case '7d':
                    start.setDate(start.getDate() - 7);
                    break;
                case '30d':
                    start.setDate(start.getDate() - 30);
                    break;
                default:
                    start.setHours(start.getHours() - 2);
            }

            const response = await axios.get(`${API_BASE_URL}/api/spapi/new-stats/queue-history`, {
                params: {
                    start: start.toISOString(),
                    end: end.toISOString(),
                    interval: queueIntervalType
                }
            });

            // Format data for recharts  
            const formattedData = response.data.map(item => ({
                timestamp: queueIntervalType === 'day'
                    ? new Date(item.timestamp).toLocaleDateString()
                    : new Date(item.timestamp).toLocaleString([], { 
                        month: 'short', 
                        day: 'numeric', 
                        hour: '2-digit', 
                        minute: '2-digit' 
                    }),
                avgQueueSize: parseFloat(item.avg_queue_size || 0),
                maxQueueSize: parseFloat(item.max_queue_size || 0)
            }));

            setQueueChartData(formattedData);
            setError(null);
        } catch (err) {
            console.error('Error fetching queue history:', err);
            setError('Failed to fetch queue history');
        } finally {
            setQueueChartLoading(false);
        }
    };

    // Fetch users who have at least one search (for the filter dropdown)
    const fetchSearchUsers = async () => {
        try {
            const response = await api.get('/api/searches/stats/users');
            setSearchUsers(response.data);
        } catch (err) {
            // Silently ignore auth errors — user may not be logged in
            console.error('Error fetching search users:', err);
        }
    };

    // Fetch search activity stats
    const fetchSearchStats = async (view, userId) => {
        setSearchChartLoading(true);
        setSearchChartError(null);
        try {
            const end = new Date();
            const start = new Date();
            switch (view) {
                case 'day':
                    start.setDate(start.getDate() - 30);
                    break;
                case 'week':
                    start.setDate(start.getDate() - 84); // 12 weeks
                    break;
                case 'month':
                    start.setFullYear(start.getFullYear() - 1); // 12 months
                    break;
                default:
                    start.setDate(start.getDate() - 30);
            }

            const params = {
                start: start.toISOString(),
                end: end.toISOString(),
                interval: view
            };
            if (userId) params.user_id = userId;

            const response = await api.get('/api/searches/stats/activity', { params });

            const labelKey = userId ? 'search_type' : 'user_name';
            const { chartData, groups } = pivotSearchData(response.data, labelKey);

            // Format period labels for display
            const formattedData = chartData.map(row => ({
                ...row,
                periodLabel: formatSearchPeriodLabel(row.period, view)
            }));

            setSearchChartData(formattedData);
            setSearchGroups(groups);
        } catch (err) {
            console.error('Error fetching search stats:', err);
            setSearchChartError('Failed to load search activity data');
        } finally {
            setSearchChartLoading(false);
        }
    };

    // Fetch ScrapingDog daily API call totals for the current month
    const fetchScrapingDogDailyStats = async () => {
        setScrapingDogChartLoading(true);
        setScrapingDogChartError(null);
        try {
            const response = await axios.get(`${API_BASE_URL}/api/spapi/new-stats/scraping-dog-daily`);
            const formattedData = response.data.map(item => ({
                dateLabel: new Date(String(item.date) + 'T00:00:00').toLocaleDateString([], {
                    month: 'short',
                    day: 'numeric'
                }),
                totalCalls: parseInt(item.total_calls || 0)
            }));

            setScrapingDogChartData(formattedData);
        } catch (err) {
            console.error('Error fetching ScrapingDog daily stats:', err);
            setScrapingDogChartError('Failed to load ScrapingDog call stats');
        } finally {
            setScrapingDogChartLoading(false);
        }
    };

    // Handle view change for search chart
    const handleSearchViewChange = (newView) => {
        setSearchView(newView);
        fetchSearchStats(newView, selectedUserId);
    };

    // Handle user filter change
    const handleSearchUserChange = (e) => {
        const userId = e.target.value;
        setSelectedUserId(userId);
        fetchSearchStats(searchView, userId);
    };

    // Handle time period change for performance chart
    const handleTimePeriodChange = (e) => {
        const newPeriod = e.target.value;
        setTimePeriod(newPeriod);
        
        // Determine interval based on period
        const newInterval = ['6h', '12h'].includes(newPeriod) ? 'hour' : 'day';
        setChartInterval(newInterval);
        
        fetchStatsDetails(newPeriod, newInterval);
    };

    // Handle time period change for queue chart
    const handleQueueTimePeriodChange = (e) => {
        const newPeriod = e.target.value;
        setQueueTimePeriod(newPeriod);
        
        // Determine interval based on period
        const newInterval = determineQueueInterval(newPeriod);
        setQueueChartInterval(newInterval);
        
        fetchQueueHistory(newPeriod, newInterval);
    };

    // Initial load - fetch users once
    useEffect(() => {
        fetchSearchUsers();
    }, []);

    // Initial load and periodic refresh - queue size and search stats (every 5 seconds)
    useEffect(() => {
        fetchQueueSize();
        fetchKeepaStats();
        fetchSearchStatsData();

        const refreshIntervalId = window.setInterval(() => {
            fetchQueueSize();
            fetchSearchStatsData();
        }, 5000);

        return () => window.clearInterval(refreshIntervalId);
        
        // Auto-refresh Keepa stats every 30 seconds
        const keepaStatsInterval = setInterval(fetchKeepaStats, 30000);
    }, []);

    // Initial load and periodic refresh - performance chart (every 5 minutes)
    useEffect(() => {
        fetchStatsDetails(timePeriod, chartInterval);

        const refreshIntervalId = window.setInterval(() => {
            fetchStatsDetails(timePeriod, chartInterval);
        }, 300000);

        return () => window.clearInterval(refreshIntervalId);
    }, [timePeriod, chartInterval]);

    // Initial load and periodic refresh - queue history (every 2 minutes)
    useEffect(() => {
        fetchQueueHistory(queueTimePeriod, queueChartInterval);

        const refreshIntervalId = window.setInterval(() => {
            fetchQueueHistory(queueTimePeriod, queueChartInterval);
        }, 120000);

        return () => window.clearInterval(refreshIntervalId);
    }, [queueTimePeriod, queueChartInterval]);

    // Initial load and refresh - search activity chart
    useEffect(() => {
        fetchSearchStats(searchView, selectedUserId);
    }, [searchView, selectedUserId]);

    // Initial load and periodic refresh - ScrapingDog daily calls chart (every 5 minutes)
    useEffect(() => {
        fetchScrapingDogDailyStats();

        const refreshIntervalId = window.setInterval(() => {
            fetchScrapingDogDailyStats();
        }, 300000);

        return () => window.clearInterval(refreshIntervalId);
    }, []);

    return (
        <div>
            <Navigation />
            <div className="stats-container">
                <div className="stats-header">
                    <h2>SP API Statistics</h2>
                </div>

                {error && <div className="error">{error}</div>}

                {/* Queue Status and Search Stats - Two Column Layout */}
                <div className="stats-grid">
                    {/* Queue Size Section */}
                    <div className="queue-section">
                        <h3>Current Queue Status</h3>
                        {loading ? (
                            <div className="loading">Loading queue data...</div>
                        ) : queueData ? (
                            <>
                                <div className="queue-summary">
                                    <div className="queue-total">
                                        <div className="label">Total Pending Requests</div>
                                        <div className="value">{queueData.total_queue_size || 0}</div>
                                        {queueData.timestamp && (
                                            <div className="timestamp">
                                                Last updated: {new Date(queueData.timestamp).toLocaleString()}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                
                                {queueData.queue_by_type && Object.keys(queueData.queue_by_type).length > 0 && (
                                    <>
                                        <h4 style={{ marginTop: '20px', marginBottom: '10px', color: '#666' }}>
                                            By Request Type
                                        </h4>
                                        <div className="queue-breakdown">
                                            {Object.entries(queueData.queue_by_type).map(([type, count]) => (
                                                <div key={type} className="queue-item">
                                                    <div className="type">{type}</div>
                                                    <div className="count">{count}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </>
                        ) : (
                            <div className="no-data">No queue data available</div>
                        )}
                    </div>

                    {/* Search Stats Section */}
                    <div className="search-stats-section">
                        <h3>Search Queue Status</h3>
                        {searchStatsLoading ? (
                            <div className="loading">Loading search stats...</div>
                        ) : searchStatsData ? (
                            <>
                                <div className="search-stats-summary">
                                    <div className="stats-row">
                                        <div className="stat-card instant">
                                            <div className="stat-header">Instant Searches</div>
                                            <div className="stat-values">
                                                <div className="stat-item">
                                                    <div className="stat-label">Running</div>
                                                    <div className="stat-value running">{searchStatsData.running_instant_searches || 0}</div>
                                                </div>
                                                <div className="stat-item">
                                                    <div className="stat-label">Pending</div>
                                                    <div className="stat-value pending">{searchStatsData.pending_instant_searches || 0}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="stats-row">
                                        <div className="stat-card scheduled">
                                            <div className="stat-header">Scheduled Searches</div>
                                            <div className="stat-values">
                                                <div className="stat-item">
                                                    <div className="stat-label">Running</div>
                                                    <div className="stat-value running">{searchStatsData.running_scheduled_searches || 0}</div>
                                                </div>
                                                <div className="stat-item">
                                                    <div className="stat-label">Pending</div>
                                                    <div className="stat-value pending">{searchStatsData.pending_scheduled_searches || 0}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    {searchStatsData.timestamp && (
                                        <div className="timestamp" style={{ marginTop: '15px', textAlign: 'center' }}>
                                            Last updated: {new Date(searchStatsData.timestamp).toLocaleString()}
                                        </div>
                                    )}
                                </div>
                            </>
                        ) : (
                            <div className="no-data">No search stats available</div>
                        )}
                    </div>
                </div>

                {/* Keepa Stats Section */}
                <div className="queue-section" style={{ marginTop: '30px' }}>
                    <h3>Keepa API Status</h3>
                    {keepaLoading ? (
                        <div className="loading">Loading Keepa stats...</div>
                    ) : keepaData ? (
                        keepaData.configured ? (
                            <>
                                <div className="queue-summary">
                                    <div className="queue-total keepa-stats-grid">
                                        <div className="keepa-stat-item">
                                            <div className="label">Pending Requests (Last Hour)</div>
                                            <div className="value keepa-value-warning">
                                                {keepaData.queue_size || 0}
                                            </div>
                                            <small className="keepa-description">Requests waiting to be processed</small>
                                        </div>
                                    </div>
                                </div>
                                {keepaData.timestamp && (
                                    <div className="timestamp keepa-timestamp">
                                        Last updated: {new Date(keepaData.timestamp).toLocaleString()}
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="no-data">
                                Keepa API not configured. Please add your Keepa API key in the Settings page.
                            </div>
                        )
                    ) : (
                        <div className="no-data">No Keepa data available</div>
                    )}
                </div>

                {/* Chart Section */}
                <div className="chart-section">
                    <h3>Performance Metrics</h3>
                    
                    <div className="chart-controls">
                        <div className="control-group">
                            <label htmlFor="timePeriod">Time Period</label>
                            <select
                                id="timePeriod"
                                value={timePeriod}
                                onChange={handleTimePeriodChange}
                            >
                                {timePeriodOptions.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        
                        <div className="control-group">
                            <label>Interval</label>
                            <div style={{ padding: '8px 0', fontSize: '14px', color: '#666' }}>
                                {chartInterval === 'hour' ? 'Hourly' : 'Daily'}
                            </div>
                        </div>
                    </div>

                    {chartLoading ? (
                        <div className="loading">Loading chart data...</div>
                    ) : chartData.length > 0 ? (
                        <div className="chart-wrapper">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={chartData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis 
                                        dataKey="timestamp" 
                                        angle={-45}
                                        textAnchor="end"
                                        height={80}
                                        tick={{ fontSize: 11 }}
                                    />
                                    <YAxis />
                                    <Tooltip />
                                    <Legend />
                                    <Line 
                                        type="monotone" 
                                        dataKey="processed" 
                                        stroke="#4caf50" 
                                        name="Processed Requests"
                                        strokeWidth={2}
                                    />
                                    <Line 
                                        type="monotone" 
                                        dataKey="failed" 
                                        stroke="#f44336" 
                                        name="Failed Requests"
                                        strokeWidth={2}
                                    />
                                    <Line 
                                        type="monotone" 
                                        dataKey="avgResponseTime" 
                                        stroke="#2196f3" 
                                        name="Avg Response Time (sec)"
                                        strokeWidth={2}
                                    />
                                    <Line 
                                        type="monotone" 
                                        dataKey="maxResponseTime" 
                                        stroke="#9c27b0" 
                                        name="Max Response Time (sec)"
                                        strokeWidth={2}
                                        strokeDasharray="5 5"
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    ) : (
                        <div className="no-data">No chart data available for the selected period</div>
                    )}
                </div>

                {/* ScrapingDog API Calls Chart */}
                <div className="chart-section" style={{ marginTop: '30px' }}>
                    <h3>ScrapingDog API Calls (Current Month)</h3>

                    {scrapingDogChartError && (
                        <div className="error" style={{ marginBottom: '10px' }}>{scrapingDogChartError}</div>
                    )}

                    {scrapingDogChartLoading ? (
                        <div className="loading">Loading ScrapingDog API calls...</div>
                    ) : scrapingDogChartData.length > 0 ? (
                        <div className="chart-wrapper">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={scrapingDogChartData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis
                                        dataKey="dateLabel"
                                        angle={-45}
                                        textAnchor="end"
                                        height={80}
                                        tick={{ fontSize: 11 }}
                                    />
                                    <YAxis allowDecimals={false} />
                                    <Tooltip />
                                    <Legend />
                                    <Line
                                        type="monotone"
                                        dataKey="totalCalls"
                                        stroke="#00897b"
                                        name="Total Calls"
                                        strokeWidth={2}
                                        dot={false}
                                        activeDot={{ r: 4 }}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    ) : (
                        <div className="no-data">No ScrapingDog call data for the current month</div>
                    )}
                </div>

                {/* Queue Size History Chart Section */}
                <div className="chart-section" style={{ marginTop: '30px' }}>
                    <h3>Queue Size History</h3>
                    
                    <div className="chart-controls">
                        <div className="control-group">
                            <label htmlFor="queueTimePeriod">Time Period</label>
                            <select
                                id="queueTimePeriod"
                                value={queueTimePeriod}
                                onChange={handleQueueTimePeriodChange}
                            >
                                {queueTimePeriodOptions.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        
                        <div className="control-group">
                            <label>Interval</label>
                            <div style={{ padding: '8px 0', fontSize: '14px', color: '#666' }}>
                                {queueChartInterval === '5min' && 'Every 5 minutes'}
                                {queueChartInterval === '30min' && 'Every 30 minutes'}
                                {queueChartInterval === '6hour' && 'Every 6 hours'}
                                {queueChartInterval === 'day' && 'Daily'}
                            </div>
                        </div>
                    </div>

                    {queueChartLoading ? (
                        <div className="loading">Loading queue history...</div>
                    ) : queueChartData.length > 0 ? (
                        <div className="chart-wrapper">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={queueChartData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis 
                                        dataKey="timestamp" 
                                        angle={-45}
                                        textAnchor="end"
                                        height={80}
                                        tick={{ fontSize: 11 }}
                                    />
                                    <YAxis />
                                    <Tooltip />
                                    <Legend />
                                    <Line 
                                        type="monotone" 
                                        dataKey="avgQueueSize" 
                                        stroke="#ff9800" 
                                        name="Avg Queue Size"
                                        strokeWidth={2}
                                    />
                                    <Line 
                                        type="monotone" 
                                        dataKey="maxQueueSize" 
                                        stroke="#f44336" 
                                        name="Max Queue Size"
                                        strokeWidth={2}
                                        strokeDasharray="5 5"
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    ) : (
                        <div className="no-data">No queue data available for the selected period</div>
                    )}
                </div>

                {/* Search Activity Chart */}
                <div className="chart-section" style={{ marginTop: '30px' }}>
                    <h3>Search Activity</h3>

                    <div className="chart-controls">
                        {/* View selector */}
                        <div className="control-group">
                            <label>View</label>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                {[
                                    { value: 'day', label: 'Daily' },
                                    { value: 'week', label: 'Weekly' },
                                    { value: 'month', label: 'Monthly' }
                                ].map(opt => (
                                    <button
                                        key={opt.value}
                                        onClick={() => handleSearchViewChange(opt.value)}
                                        style={{
                                            padding: '7px 14px',
                                            fontSize: '14px',
                                            border: '1px solid #ddd',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            background: searchView === opt.value ? '#1976d2' : 'white',
                                            color: searchView === opt.value ? 'white' : '#333',
                                            fontWeight: searchView === opt.value ? 600 : 400
                                        }}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* User filter */}
                        {searchUsers.length > 0 && (
                            <div className="control-group">
                                <label htmlFor="searchUserFilter">User</label>
                                <select
                                    id="searchUserFilter"
                                    value={selectedUserId}
                                    onChange={handleSearchUserChange}
                                >
                                    <option value="">All Users</option>
                                    {searchUsers.map(u => (
                                        <option key={u.id} value={u.id}>{u.name}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* Legend hint */}
                        <div className="control-group">
                            <label>Grouped by</label>
                            <div style={{ padding: '8px 0', fontSize: '14px', color: '#666' }}>
                                {selectedUserId ? 'Search type (scheduled / instant)' : 'User'}
                            </div>
                        </div>
                    </div>

                    {searchChartError && (
                        <div className="error" style={{ marginBottom: '10px' }}>{searchChartError}</div>
                    )}

                    {searchChartLoading ? (
                        <div className="loading">Loading search activity...</div>
                    ) : searchChartData.length > 0 ? (
                        <div className="chart-wrapper">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={searchChartData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis
                                        dataKey="periodLabel"
                                        angle={-45}
                                        textAnchor="end"
                                        height={80}
                                        tick={{ fontSize: 11 }}
                                    />
                                    <YAxis allowDecimals={false} />
                                    <Tooltip />
                                    <Legend />
                                    {searchGroups.map((group, idx) => {
                                        const color = selectedUserId
                                            ? (SEARCH_TYPE_COLORS[group] || USER_COLORS[idx % USER_COLORS.length])
                                            : USER_COLORS[idx % USER_COLORS.length];
                                        return (
                                            <Line
                                                key={group}
                                                type="monotone"
                                                dataKey={group}
                                                stroke={color}
                                                name={selectedUserId
                                                    ? (group === 'scheduled' ? 'Scheduled' : 'Instant')
                                                    : group}
                                                strokeWidth={2}
                                                dot={false}
                                                activeDot={{ r: 4 }}
                                            />
                                        );
                                    })}
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    ) : (
                        <div className="no-data">No search activity for the selected period</div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default Stats;
